'use client';

import React, { useState } from 'react';
import { Cpu, ChevronUp, ChevronDown, Minus } from 'lucide-react';
import { useSession } from './SessionProvider';

function SQIBar({ value, status }: { value: number; status: string }) {
  const color =
    status === 'good' ? 'bg-emerald-400' : status === 'fair' ? 'bg-amber-400' : status === 'poor' ? 'bg-red-400' : 'bg-zinc-600';
  return (
    <div className="w-full h-1.5 rounded-full bg-zinc-800/80 overflow-hidden">
      <div
        className={`h-full rounded-full transition-all duration-500 ${color}`}
        style={{ width: `${value}%` }}
      />
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const classes = {
    good: 'bg-emerald-950/60 border-emerald-800/50 text-emerald-400',
    fair: 'bg-amber-950/60 border-amber-800/50 text-amber-400',
    poor: 'bg-red-950/60 border-red-800/50 text-red-400',
    disconnected: 'bg-zinc-900/60 border-zinc-700/50 text-zinc-500',
  }[status] ?? 'bg-zinc-900 border-zinc-700 text-zinc-500';

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border uppercase tracking-wide ${classes}`}>
      {status}
    </span>
  );
}

type SortKey = 'label' | 'sqi' | 'impedance' | 'amplitude' | 'status';
type SortDir = 'asc' | 'desc';

export default function ChannelQualityGrid() {
  const { channels } = useSession();
  const [sortKey, setSortKey] = useState<SortKey>('label');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sorted = [...channels].sort((a, b) => {
    let av: string | number = a[sortKey] as string | number;
    let bv: string | number = b[sortKey] as string | number;
    if (typeof av === 'string') av = av.toLowerCase();
    if (typeof bv === 'string') bv = bv.toLowerCase();
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  function SortIcon({ col }: { col: SortKey }) {
    if (sortKey !== col) return <Minus size={10} className="text-zinc-700" />;
    return sortDir === 'asc' ? (
      <ChevronUp size={10} className="text-emerald-400" />
    ) : (
      <ChevronDown size={10} className="text-emerald-400" />
    );
  }

  const goodCount = channels.filter((c) => c.status === 'good').length;
  const poorCount = channels.filter((c) => c.status === 'poor' || c.status === 'disconnected').length;

  return (
    <div className="rounded-xl border border-zinc-800/60 bg-zinc-900/40 p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-lg bg-zinc-900 border border-zinc-800 flex items-center justify-center">
            <Cpu size={14} className="text-sky-400" />
          </div>
          <div>
            <p className="text-[13px] font-semibold text-zinc-200">Electrode Channel Quality</p>
            <p className="text-[11px] text-zinc-600">
              {goodCount}/8 channels optimal ·{' '}
              {poorCount > 0 ? (
                <span className="text-red-400">{poorCount} channel{poorCount > 1 ? 's' : ''} degraded</span>
              ) : (
                <span className="text-emerald-400">All channels within range</span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {[
            { label: 'Good', color: 'bg-emerald-400', count: channels.filter((c) => c.status === 'good').length },
            { label: 'Fair', color: 'bg-amber-400', count: channels.filter((c) => c.status === 'fair').length },
            { label: 'Poor', color: 'bg-red-400', count: channels.filter((c) => c.status === 'poor').length },
          ].map((s) => (
            <div key={`ch-stat-${s.label}`} className="flex items-center gap-1 px-2 py-0.5 rounded bg-zinc-900/60 border border-zinc-800/50">
              <span className={`w-1.5 h-1.5 rounded-full ${s.color}`} />
              <span className="text-[11px] text-zinc-400">{s.count} {s.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-[12px]">
          <thead>
            <tr className="border-b border-zinc-800/60">
              {(
                [
                  { key: 'label', label: 'Channel' },
                  { key: 'location', label: 'Location', sortable: false },
                  { key: 'sqi', label: 'SQI' },
                  { key: 'impedance', label: 'Impedance (kΩ)' },
                  { key: 'amplitude', label: 'Amplitude (µV)' },
                  { key: 'status', label: 'Status' },
                ] as Array<{ key: string; label: string; sortable?: boolean }>
              ).map((col) => (
                <th
                  key={`th-${col.key}`}
                  className={`text-left pb-2 pr-4 font-medium text-zinc-500 tracking-wide text-[11px] uppercase whitespace-nowrap ${
                    col.sortable !== false ? 'cursor-pointer hover:text-zinc-300 transition-colors select-none' : ''
                  }`}
                  onClick={col.sortable !== false ? () => handleSort(col.key as SortKey) : undefined}
                >
                  <div className="flex items-center gap-1">
                    {col.label}
                    {col.sortable !== false && <SortIcon col={col.key as SortKey} />}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((ch) => (
              <tr
                key={ch.id}
                className="border-b border-zinc-800/30 hover:bg-zinc-800/20 transition-colors duration-100 group"
              >
                <td className="py-2.5 pr-4">
                  <span className="font-mono font-semibold text-zinc-200 text-[13px]">{ch.label}</span>
                </td>
                <td className="py-2.5 pr-4 text-zinc-500 whitespace-nowrap">{ch.location}</td>
                <td className="py-2.5 pr-4">
                  <div className="flex items-center gap-2 min-w-[80px]">
                    <span className="font-mono tabular-nums text-zinc-300 w-7 text-right">{ch.sqi}</span>
                    <div className="w-16">
                      <SQIBar value={ch.sqi} status={ch.status} />
                    </div>
                  </div>
                </td>
                <td className="py-2.5 pr-4">
                  <span
                    className={`font-mono tabular-nums ${
                      ch.impedance > 25 ? 'text-red-400' : ch.impedance > 10 ? 'text-amber-400' : 'text-zinc-300'
                    }`}
                  >
                    {ch.impedance.toFixed(1)}
                  </span>
                </td>
                <td className="py-2.5 pr-4">
                  <span className="font-mono tabular-nums text-zinc-300">{ch.amplitude.toFixed(1)}</span>
                </td>
                <td className="py-2.5">
                  <StatusBadge status={ch.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}